<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { isUidOfType, splitUid, toUid, type Uid } from "@/helpers/utility/Id";
import { uniq } from "lodash-es";
import { notNull, isInList, type HasProperty } from "@/helpers/utility/Type";
import { getBoundingBox } from "@/helpers/math/geometry";
import { getContrastColor, toHSLA } from "@/helpers/utility/Color";
import {
  fileToDataURL,
  getDataUrlDimensions,
  warnIfFileIsLarge
} from "@/helpers/utility/File";
import { eventHub } from "@/helpers/utility/Events";
import { getLineCenter } from "@editor/items/relation/relationFunctions";

import usePathsUpdater from "@editor/items/relation/usePathsUpdater";
import { useUndo } from "@editor/store/undo/undo.store";
import { useModelStore } from "@editor/items/model/model.store";
import { useImageStore } from "@editor/items/image/images.store";
import { useFlowBranchStore } from "@editor/items/flow/flowBranches.store";
import { useFeatures } from "@editor/store/features.store";
import { useIndexStore } from "@editor/items/index/indices.store";
import { useTargetStore } from "@editor/items/target/targets.store";
import { useResourceStore } from "@editor/items/resource/resources.store";
import { useSettingsStore } from "@editor/store/settings.store";
import { useSceneNodesStore } from "@editor/items/scene/sceneNodes.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";
import { useRelationSelectionPoints } from "@editor/items/relation/useRelationSelectionPoints";
import { useSceneShapeStore } from "@editor/items/sceneShape/sceneShapes.store";
import { useSceneRelationSettingsStore } from "@editor/items/relation/sceneRelationSettings.store";
import { useSceneFlowNodeStore } from "@editor/items/flow/sceneFlowNodes";
import { isElementTreeNode, isSceneElementNode } from "@editor/items/sceneElement/sceneElementFunctions";
import { isSceneTextNode } from "@editor/items/sceneText/sceneTextFunctions";
import { isSceneShapeNode } from "@editor/items/sceneShape/sceneShapeFunctions";
import { isSceneMediumNode } from "@editor/items/sceneMedium/sceneMediumFunctions";
import { isLayerTreeNode } from "@editor/items/sceneLayer/sceneLayerFunctions";
import { isGroupTreeNode } from "@editor/items/sceneGroup/sceneGroupFunctions";
import { getAllDescendantUids, getNodeDimensions } from "@editor/items/scene/sceneNodeFunctions";
import { isSceneChartNode } from "@editor/items/sceneChart/sceneChartFunctions";

import LucideIcon from "@core/ui/LucideIcon.vue";
import NetActionbar from "./NetActionbar.vue";
import ButtonPopout from "@core/input/ButtonPopout.vue";
import ColorPicker from "@core/input/ColorPicker.vue";
import InputRange from "@core/input/InputRange.vue";
import TooltipButton from "@core/ui/TooltipButton.vue";
import InputColorText from "@core/input/InputColorText.vue";
import FillSymbol from "@core/ui/FillSymbol.vue";
import {
  AlignCenterHorizontalIcon,
  AlignCenterVerticalIcon,
  AlignEndHorizontalIcon,
  AlignEndVerticalIcon,
  AlignHorizontalSpaceBetweenIcon,
  AlignStartHorizontalIcon,
  AlignStartVerticalIcon,
  AlignVerticalSpaceBetweenIcon
} from "@lucide/vue";

import type { ItemOfStore, ItemType } from "@editor/items/ItemOfStore";
import type { ItemStoreActions } from "@editor/items/itemStore.store";
import type { AreaItem } from "@editor/items/area/AreaItem";
import type { MomentumItem } from "@editor/items/momentum/MomentumItem";
import type { TargetItem } from "@editor/items/target/TargetItem";
import type { ResourceItem } from "@editor/items/resource/ResourceItem";
import type { IndexItem } from "@editor/items/index/IndexItem";
import type { FlowItem } from "@editor/items/flow/FlowItem";
import type { FlowBranchItem } from "@editor/items/flow/FlowBranchItem";
import type { RelationItem } from "@editor/items/relation/RelationItem";
import type { CanvasSelection } from "./NetCanvasProps";
import type { NetToolKey } from "./NetTools";
import type { StoreItem } from "@editor/items/StoreItem";
import type { SceneTreeItemSize, SceneTreeNode } from "@editor/items/scene/SceneTreeItem";
import type { SceneNodeItem } from "@editor/items/scene/SceneNodeItem";
import type { SceneNodeType } from "@editor/items/scene/SceneNodeData";
import type { SceneShapeData } from "@editor/items/sceneShape/sceneShapeData";

const props = defineProps<{
  selection: CanvasSelection;
  itemSizes: { [uid: Uid<"sceneNode" | "flow">]: SceneTreeItemSize };
  tool: NetToolKey;
  impactSourceElement?:
    | { uid: Uid<"target"> }
    | { uid: Uid<"resource"> }
    | { uid: Uid<"index"> };
  flowSourceElement?: { uid: Uid<"target"> } | { uid: Uid<"resource"> };
}>();

const emit = defineEmits<{
  (e: "update:tool", tool: NetToolKey): void;
  (
    e: "toggle-relation-mode",
    item: { uid: Uid<"target" | "resource" | "index"> }
  ): void;
  (
    e: "toggle-flow-mode",
    item: { uid: Uid<"target" | "resource"> }
  ): void;
  (e: "popupmenu", payload: { uid: Uid<"sceneNode" | "relation" | "flowBranch" | "flow" | "target" | "resource" | "index"> }): void;
  (e: "select", item: { uid: Uid<"sceneNode" | "flow" | "relation" | "flowBranch" | "momentum"> }): void;
  (e: "wheel", event: WheelEvent): void;
}>();

type ItemKey =
  | keyof AreaItem
  | keyof MomentumItem
  | keyof TargetItem
  | keyof ResourceItem
  | keyof IndexItem
  | keyof FlowItem
  | keyof FlowBranchItem
  | keyof RelationItem
  | keyof SceneNodeItem;

const modelStore = useModelStore();
const undoStore = useUndo();
const features = useFeatures();
const sceneTree = useSceneTree();
const sceneNodeStore = useSceneNodesStore();

const { selectionPoints } = useRelationSelectionPoints();

const sceneItemTypes = [
  "sceneNode",
  "flow",
  "relation",
  "flowBranch",
  "momentum"
] as const;

const selectedItems = computed(() => {
  return [...props.selection.selected.values()]
    .map((uid) => {
      const { type, id } = splitUid(uid);
      if (!isInList(type, sceneItemTypes)) return null;
      return modelStore.getItem(type, id);
    })
    .filter(notNull);
});

const selectedItem = computed(() => {
  if (selectedItems.value.length !== 1) return null;
  return selectedItems.value[0];
});

const selectedSceneNode = computed(() => {
  if (selectedItem.value && selectedItem.value.type == "sceneNode") {
    return selectedItem.value as SceneNodeItem;
  }
  return null;
});

const { optimizedPathLines } = usePathsUpdater();

const dimensions = computed(() => {
  if (selectedItemSizes.value.length === 0)
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      left: 0,
      top: 0,
      right: 0,
      bottom: 0
    };

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  selectedItemSizes.value.forEach((item) => {
    if (item.type == "flow") {
      minX = Math.min(minX, item.x - 10);
      minY = Math.min(minY, item.y - 10);
      maxX = Math.max(maxX, item.x + 10);
      maxY = Math.max(maxY, item.y + 10);
      return;
    }
    const boundingBox = getBoundingBox(item);
    minX = Math.min(minX, boundingBox.left);
    minY = Math.min(minY, boundingBox.top);
    maxX = Math.max(maxX, boundingBox.right);
    maxY = Math.max(maxY, boundingBox.bottom);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    left: minX,
    top: minY,
    right: maxX,
    bottom: maxY
  };
});


const selectedItemSizes = computed(
  (): SceneTreeItemSize<SceneNodeType | "flow" | "relation">[] => {
    return selectedItems.value
      .map((item): SceneTreeItemSize<SceneNodeType | "flow" | "relation"> | null => {
        if (item.type == "flow" || item.type == "sceneNode") {
          return props.itemSizes[item.uid];
        }

        if (item.type === "momentum") {
          const elementUid = toUid(item.elementType, item.elementId);
          const node = sceneTree.elementNodes.get(elementUid);
          if (!node) return null;
          const size = props.itemSizes[node.uid];
          return {
            ...size,
            x: size.x - 20
          };
        }

        if (item.type === "relation") {
          const point = selectionPoints.value.get(item.uid);
          if (point) {
            return <SceneTreeItemSize<"relation">>{
              type: "relation",
              uid: item.uid,
              x: point.x,
              y: point.y + 15,
              width: 10,
              height: 10,
              left: point.x,
              top: point.y + 15,
              right: point.x + 10,
              bottom: point.y + 25,
              center: { x: point.x + 5, y: point.y + 20 },
              locked: false,
              data: {},
              aspectRatioLocked: true
            };
          } else {
            const center = getLineCenter(optimizedPathLines.value[item.uid]);
            return <SceneTreeItemSize<"relation">>{
              type: "relation",
              ...center,
              width: 10,
              height: 10,
              left: center.x,
              top: center.y,
              right: center.x + 10,
              bottom: center.y + 10,
              center: { x: center.x + 5, y: center.y + 5 },
              uid: item.uid,
              locked: false,
              data: {},
              aspectRatioLocked: true
            };
          }
        }

        // if (item.type == "target" || item.type == "resource" || item.type == "index") {
        //   const node = sceneTree.elementNodes.get(item.uid);
        //   if (!node) return null;
        //   return props.itemSizes[node.uid];
        // }

        return null;
      })
      .filter(notNull);
  }
);

function updateKey<
  T extends ItemType,
  K extends keyof I,
  I extends ItemOfStore<T> = ItemOfStore<T>
>(item: I, key: K, value: I[K]) {
  const type = (item as StoreItem).type as T;
  const id = (item as StoreItem).id as I["id"];
  const store = modelStore.getStore(type) as unknown as ItemStoreActions<I>;
  if (!store) throw new Error(`Store for type ${type} not found`);
  return store.updateKey(id, key, value);
}
function setValues(key: ItemKey, value: any) {
  const groupId = undoStore.startGroup("model", "setValues");
  for (const item of selectedItems.value) {
    if (key in item) {
      updateKey(item, key as keyof typeof item, value);
    }
  }
  undoStore.endGroup("model", groupId);
}

// =====================================================================
// PROPERTIES

const supportedSceneNodeProps = {
  text: ["textColor", "fontSizeRelative", "locked", "gridLocked"],
  element: ["fill", "locked", "fontSizeRelative"],
  shape: ["fill", "stroke", "strokeWidth", "locked", "gridLocked"],
  medium: ["locked", "gridLocked"],
  group: ["locked", "gridLocked"],
  layer: ["locked", "gridLocked"],
  chart: ["locked", "gridLocked", "fontSizeRelative"]
};

function supportsProperty<
  T extends SceneNodeItem | MomentumItem | FlowItem | FlowBranchItem | RelationItem,
  K extends PropertyKey
>(item: T, property: K): item is HasProperty<T, K> {
  if (!(property in item)) return false;
  if (item.type === "sceneNode") {
    const nodeType = item.dataType;
    return isInList(property, supportedSceneNodeProps[nodeType]);
  }
  return true;
}

const hasLocked = computed(() => {
  return selectedItems.value.some((item) => supportsProperty(item, "locked"));
});
const locked = computed({
  get() {
    return selectedItems.value.some((item) => supportsProperty(item, "locked") && item.locked);
  },
  set(value) {
    setValues("locked", value);
  }
});

const hasGridLocked = computed(() => {
  return selectedItems.value.some((item) => supportsProperty(item, "gridLocked"));
});
const gridLocked = computed({
  get() {
    return selectedItems.value.some((item) => supportsProperty(item, "gridLocked") && item.gridLocked);
  },
  set(value) {
    alignSelectedItemsToGrid();
    setValues("gridLocked", value);
  }
});

const hasFill = computed(() => {
  return selectedItems.value.some((n) => supportsProperty(n, "fill"));
});
const fills = computed(() => {
  const itemsWithFill = selectedItems.value.filter((item) => supportsProperty(item, "fill"));
  const allFills = itemsWithFill.map((item) => item.fill ?? null);
  return uniq(allFills);
});
const fill = computed({
  get() {
    return fills.value.length === 1 ? fills.value[0] : null;
  },
  set(value) {
    // preserve the alpha channel of the fill color
    const fillColor = fill.value;
    const fillHSLA = fillColor ? toHSLA(fillColor) : { a: 1 };
    let newFillColor = null;
    if (value) {
      const newFillHSLA = toHSLA(value);
      newFillHSLA.a = fillHSLA.a; // Preserve the original alpha
      newFillColor = `hsla(${newFillHSLA.h}, ${newFillHSLA.s * 100}%, ${newFillHSLA.l * 100}%, ${newFillHSLA.a})`;
    }

    const groupId = undoStore.startGroup("model", "setFill");
    for (const item of selectedItems.value) {
      if ("fill" in item) {
        updateKey(item, "fill", newFillColor);
      }
    }
    undoStore.endGroup("model", groupId);
  }
});
const fillFullOpacity = computed(() => {
  // get the fill color with full opacity
  const fillColor = fill.value || "#00000000";
  const fillHSLA = toHSLA(fillColor);
  return `hsla(${fillHSLA.h}, ${fillHSLA.s * 100}%, ${fillHSLA.l * 100}%, 1)`;
});
const fillOpacity = computed({
  get() {
    const fillColor = fill.value;
    if (fillColor == null) return 100;
    const fillHSLA = toHSLA(fillColor);
    return fillHSLA.a * 100;
  },
  set(value: number) {
    const fillColor = fill.value;
    const fillHSLA = fillColor ? toHSLA(fillColor) : { h: 0, s: 0, l: 0, a: 1 };
    const hue = fillHSLA.h;
    const saturation = fillHSLA.s;
    const lightness = fillHSLA.l;
    const newOpacity = value / 100;
    const newFillColor = `hsla(${hue}, ${saturation * 100}%, ${lightness * 100}%, ${newOpacity})`;

    const groupId = undoStore.startGroup("model", "setFillOpacity");
    for (const item of selectedItems.value) {
      if ("fill" in item) {
        updateKey(item, "fill", newFillColor);
      }
    }
    undoStore.endGroup("model", groupId);
  }
});

const hasStroke = computed(() => {
  return selectedItems.value.some((item) => supportsProperty(item, "stroke"));
});
const stroke = computed({
  get() {
    const strokes = selectedItems.value
      .map((item) => {
        if (supportsProperty(item, "stroke")) return item.stroke;
        return null;
      })
      .filter(notNull);
    return strokes.length === 0 ? "#000000" : strokes[0];
  },
  set(value) {
    if (value == null) return;
    setValues("stroke", value);
  }
});
const strokeContrastColor = computed(() => {
  // Calculate a contrasting color for the stroke
  const strokeColor = stroke.value;
  return getContrastColor(strokeColor);
});
const strokeWidth = computed({
  get() {
    const strokeWidths = selectedItems.value
      .map((item) => {
        if (supportsProperty(item, "strokeWidth")) return item.strokeWidth;
        return null;
      })
      .filter(notNull);
    return strokeWidths.length === 0 ? 1 : strokeWidths[0];
  },
  set(value) {
    if (value == null) return;
    setValues("strokeWidth", value);
  }
});

const hasTextColor = computed(() => {
  return selectedItems.value.some((item) => supportsProperty(item, "textColor"));
});
const textColors = computed(() => {
  const itemsWithTextColor = selectedItems.value.filter(
    (item) => supportsProperty(item, "textColor")
  );
  const allTextColors = itemsWithTextColor.map((item) => {
    if (supportsProperty(item, "textColor")) return item.textColor ?? null;
    return null;
  });
  return uniq(allTextColors);
});
const textColor = computed({
  get(): string | null {
    return textColors.value.length === 1 ? textColors.value[0] : null;
  },
  set(value: string | null) {
    setValues("textColor", value);
  }
});
const contrastTextColor = computed(() => {
  return "var(--color-text)";
});

// ======================
// FONT SIZE

const sceneFontSize = computed(() => sceneTree.activeScene.fontSize);
const hasFontSize = computed(() => selectedItems.value.some((item) => supportsProperty(item, "fontSizeRelative")));
const fontSize = computed({
  get(): number | undefined {
    const fontSizes = selectedItems.value
      .map((item) => {
        if (supportsProperty(item, "fontSizeRelative")) return item.fontSizeRelative * sceneFontSize.value;
        return null;
      })
      .filter(notNull);
    return fontSizes.length === 0 ? sceneFontSize.value : fontSizes[0];
  },
  set(value: number | undefined) {
    if (value == null) return;
    const groupId = undoStore.startGroup("model", "setValues");
    for (const item of selectedItems.value) {
      if ("fontSizeRelative" in item) {
        updateKey(item, "fontSizeRelative", value / sceneFontSize.value);
      } else if ("fontSize" in item) {
        updateKey(item, "fontSize", value);
      }
    }
    undoStore.endGroup("model", groupId);
  }
});
function increaseFontSize() {
  fontSize.value = (fontSize.value ?? sceneFontSize.value) + 2;
}
function decreaseFontSize() {
  fontSize.value = Math.max((fontSize.value ?? sceneFontSize.value) - 2, 2);
}
function resetFontSize() {
  fontSize.value = sceneFontSize.value;
}

// ====================
// MEDIA

const imageStore = useImageStore();

const hasMedium = computed(() => {
  return selectedItems.value.some(
    (item) => {
      if (item.type !== "sceneNode") return false;
      return (item.dataType == 'medium') || ("imageId" in item.data);
    }
  );
});

function onMediaFileChange(event: Event) {
  const file = (event.target as HTMLInputElement).files?.[0];
  if (!file) return;

  overrideMedium(file);
}

async function overrideMedium(file: File | null) {
  if (!file) return;

  const type = file.type;

  if (!type.startsWith("image") && !type.startsWith("video")) {
    throw new Error("Only images and videos are allowed");
  }

  warnIfFileIsLarge(file, 4); // Warn if the file is larger than 4MB

  const dataURL = await fileToDataURL(file);
  const mediumDimensions = await getDataUrlDimensions(dataURL, type);
  console.log("dimensions", mediumDimensions);

  const aspectRatio = mediumDimensions.width / mediumDimensions.height;

  const groupId = undoStore.startGroup("model", "overrideMedium");
  for (const item of selectedItems.value) {
    if (item.type === "sceneNode") {
      const node = item;
      if (isSceneMediumNode(node)) {
        const width = item.width;
        const newHeight = width / aspectRatio;
        sceneNodeStore.update({
          id: node.id,
          data: {
            data: dataURL,
            mediaType: file.type,
          },
          height: newHeight
        });
      } else if (isSceneElementNode(node)) {
        if (node.data.imageId != null) {
          imageStore.update({
            id: node.data.imageId,
            data: dataURL,
            aspectRatio: aspectRatio,
            mediaType: file.type,
            width: mediumDimensions.width,
            height: mediumDimensions.height
          });
        } else {
          const imageEntry = await imageStore.create({
            data: dataURL,
            aspectRatio: aspectRatio,
            mediaType: file.type,
            width: mediumDimensions.width,
            height: mediumDimensions.height
          });
          sceneNodeStore.updateDataKey<"element">(node.id, "imageId", imageEntry.id);
        }
      }
    } else if ("imageId" in item) {
      if (item.imageId != null) {
        imageStore.update({
          id: item.imageId,
          data: dataURL,
          aspectRatio: aspectRatio,
          // mediaType: file.type,
          width: mediumDimensions.width,
          height: mediumDimensions.height
        });
      } else {
        const imageEntry = await imageStore.create({
          data: dataURL,
          aspectRatio: aspectRatio,
          // mediaType: file.type,
          width: mediumDimensions.width,
          height: mediumDimensions.height
        });
        updateKey(item, "imageId", imageEntry.id);
      }
    }
  }
  undoStore.endGroup("model", groupId);
}

const popupTypes = [
  "target",
  "resource",
  "index",
  "flow",
  "flowBranch",
  "relation",
  "momentum"
] as const;
function canOpenPopupmenu(item: ItemOfStore<ItemType> | null): boolean {
  if (!item) return false;
  if (item.type === "sceneNode") {
    if (isSceneElementNode(item)) {
      return true;
    }
    if (isSceneChartNode(item)) {
      return true;
    }
    return false;
  }
  return isInList(item.type, popupTypes);
}
const popupMenuAvailable = computed(() => {
  // console.log("check popup menu", selectedItem.value?.uid);
  return canOpenPopupmenu(selectedItem.value);
});
function openPopupmenu(item: ItemOfStore<ItemType> | null) {
  if (!item) return;
  if (!canOpenPopupmenu(item)) return;
  eventHub.emit("popupmenu-request", item.uid);
}

async function deleteItems() {
  const groupId = undoStore.startGroup("model", "deleteItems");
  for (const item of selectedItems.value) {
    const type = item.type;
    const id = item.id;
    const store = modelStore.getStore(type) as unknown as ItemStoreActions<
      typeof item
    >;
    if (store) {
      await store.delete({ id });
    }
  }
  undoStore.endGroup("model", groupId);
}

// =============================
// Type-specific code

// === ELEMENTS ===

const indexStore = useIndexStore();
const targetStore = useTargetStore();
const resourceStore = useResourceStore();

const availableElementTypes = computed(() => {
  const types: ("target" | "resource" | "index")[] = [];
  if (features.target) types.push("target");
  if (features.resource) types.push("resource");
  if (features.index) types.push("index");
  return types;
});

function changeElementType(
  elementUid: Uid<"target" | "resource" | "index">,
  newType: "target" | "index" | "resource"
) {
  const { type, id } = splitUid(elementUid);
  if (type === "index") {
    if (newType == "index") return;
    indexStore.changeType(id, newType);
  } else if (type === "target") {
    if (newType == "target") return;
    targetStore.changeType(id, newType);
  } else if (type === "resource") {
    if (newType == "resource") return;
    resourceStore.changeType(id, newType);
  }
}
// FLOW CREATION

async function createVoidFlow(uid: Uid<"target" | "resource" | "index">, type: "source" | "sink") {
  const { type: itemType, id } = splitUid(uid);
  
  if (itemType === "index") return;

  const item = {
    type: itemType,
    id,
    uid
  }

  const baseItem = type === "sink" ? item : null;
  const endItem = type === "source" ? item : null;

  const { flow } = await useFlowBranchStore().createFlowBetween(
    baseItem,
    endItem
  );

  emit("toggle-flow-mode", { uid: item.uid as Uid<"target" | "resource"> });
  emit("select", flow);
}

// === SHAPES ===
const sceneShapeStore = useSceneShapeStore();
const shapeTypes = sceneShapeStore.shapeTypes;

function changeShapeType(newType: SceneShapeData["shapeType"]) {
  if (!selectedSceneNode.value || !isSceneShapeNode(selectedSceneNode.value)) return;
  sceneShapeStore.convertShapeType(selectedSceneNode.value.id, newType);
}

const editingShape = computed(() => {
  return props.tool === "shapeedit";
});

function editShape() {
  if (!selectedSceneNode.value) return;
  eventHub.emit("scene-shape-edit-requested", selectedSceneNode.value.uid);
}
const onHandle = ref(false);
function onPointerDown(event: PointerEvent) {
  const path = event.composedPath();
  onHandle.value = path.some(
    (el) => el instanceof SVGElement && el.classList.contains("editing-handle")
  );
}
function onPointerUp(event: PointerEvent) {
  onHandle.value = false;
}
onMounted(() => {
  document.addEventListener("pointerdown", onPointerDown, { capture: true });
  document.addEventListener("pointerup", onPointerUp, { capture: true });
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onPointerDown, { capture: true });
  document.removeEventListener("pointerup", onPointerUp, { capture: true });
});

const editingRadius = computed(() => {
  return props.tool === "radius";
});

function editRadius() {
  if (props.tool == "radius") {
    emit("update:tool", "pointer");
  } else {
    emit("update:tool", "radius");
  }
}

// ==========================
// RELATIONS

const relationSettingsStore = useSceneRelationSettingsStore();
function isRelationHidden(relationId: number): boolean {
  const settings = relationSettingsStore.byScene[sceneTree.activeScene.id]?.[relationId];
  return settings ? settings.visible === false : false;
}
function hideRelation(relationId: number) {
  relationSettingsStore.updateOrCreate({
    relationId,
    sceneId: sceneTree.activeScene.id,
    visible: false
  });
}
function showRelation(relationId: number) {
  relationSettingsStore.updateOrCreate({
    relationId,
    sceneId: sceneTree.activeScene.id,
    visible: true
  });
}

function showRelations() {
  const groupId = undoStore.startGroup("model", "showRelations");
  for (const item of selectedItems.value) {
    if (item.type === "relation") {
      showRelation(item.id);
    }
  }
  undoStore.endGroup("model", groupId);
}
function hideRelations() {
  const groupId = undoStore.startGroup("model", "hideRelations");
  for (const item of selectedItems.value) {
    if (item.type === "relation") {
      hideRelation(item.id);
    }
  }
  undoStore.endGroup("model", groupId);
}

// ==========================
// FLOWS

const sceneFlowNodeStore = useSceneFlowNodeStore();
function isFlowHidden(flowId: number): boolean {
  const node = sceneFlowNodeStore.byScene[sceneTree.activeScene.id]?.[flowId];
  return node ? node.visible === false : false;
}
function hideFlow(flowId: number) {
  sceneFlowNodeStore.updateOrCreate({
    flowId,
    sceneId: sceneTree.activeScene.id,
    visible: false
  });
}
function showFlow(flowId: number) {
  sceneFlowNodeStore.updateOrCreate({
    flowId,
    sceneId: sceneTree.activeScene.id,
    visible: true
  });
}
function showFlows() {
  const groupId = undoStore.startGroup("model", "showFlows");
  for (const item of selectedItems.value) {
    if (item.type === "flow") {
      showFlow(item.id);
    }
  }
  undoStore.endGroup("model", groupId);
}
function hideFlows() {
  const groupId = undoStore.startGroup("model", "hideFlows");
  for (const item of selectedItems.value) {
    if (item.type === "flow") {
      hideFlow(item.id);
    }
  }
  undoStore.endGroup("model", groupId);
}

// ==========================
// Alignment

const alignableNodes = computed(() => {
  const selectedSceneNodes = [...props.selection.selected.values()]
    .filter((uid) => {
      return isUidOfType(uid, "sceneNode");
    })
    .map((uid) => sceneTree.treeNodes.get(uid))
    .filter(notNull);

  const selectedGroupNodes = selectedSceneNodes.filter((node) => isGroupTreeNode(node));

  const nestedUids = new Set<string>();
  for (const groupNode of selectedGroupNodes) {
    const groupNestedUids = getAllDescendantUids(groupNode.node);
    for (const uid of groupNestedUids) {
      nestedUids.add(uid);
    }
  }

  const itemsAndGroups = [...selectedSceneNodes];

  // exclude items that are inside selected groups
  const filteredItems = itemsAndGroups.filter(
    (item) => !nestedUids.has(item.uid)
  );

  return filteredItems;
});

const alignableSizedNodes = computed(() => {
  return alignableNodes.value.map((node) => {
    const size = getNodeDimensions(node);
    return {
      ...node,
      size
    };
  });
});

function snapsToGrid(node: SceneTreeNode): boolean {
  if (isGroupTreeNode(node)) {
    return node.children.some((child) =>
      snapsToGrid(child)
    );
  }
  return isElementTreeNode(node);
}

function alignHorizontalStart() {
  if (alignableSizedNodes.value.length === 0) return;
  const undoGroupId = undoStore.startGroup("model", "alignVerticalStart");
  const smallestX = Math.min(...alignableSizedNodes.value.map((i) => i.size.x));
  for (const node of alignableSizedNodes.value) {
    const rounded = snapsToGrid(node)
      ? Math.round(smallestX / 10) * 10
      : smallestX;

    changeSize(node, { x: rounded - node.size.x });
  }
  undoStore.endGroup("model", undoGroupId);
}

function alignHorizontalCenter() {
  if (alignableSizedNodes.value.length === 0) return;
  const groupId = undoStore.startGroup("model", "alignVerticalCenter");
  const smallestX = Math.min(...alignableSizedNodes.value.map((i) => i.size.x));
  const largestX = Math.max(
    ...alignableSizedNodes.value.map((i) => i.size.x + i.size.width)
  );
  const centerX = (smallestX + largestX) / 2;
  for (const node of alignableSizedNodes.value) {
    const itemSize = node.size;
    const rounded = snapsToGrid(node)
      ? Math.round((centerX - itemSize.width / 2) / 10) * 10
      : Math.round(centerX - itemSize.width / 2);

    changeSize(node, { x: rounded - node.size.x });
  }
  undoStore.endGroup("model", groupId);
}

function alignHorizontalEnd() {
  if (alignableSizedNodes.value.length === 0) return;
  const groupId = undoStore.startGroup("model", "alignVerticalEnd");
  const largestX = Math.max(
    ...alignableSizedNodes.value.map((i) => i.size.x + i.size.width)
  );
  for (const node of alignableSizedNodes.value) {
    const rounded = snapsToGrid(node)
      ? Math.round(largestX / 10) * 10
      : largestX;

    changeSize(node, { x: rounded - node.size.x - node.size.width });
  }
  undoStore.endGroup("model", groupId);
}

function alignVerticalStart() {
  if (alignableSizedNodes.value.length === 0) return;
  const groupId = undoStore.startGroup("model", "alignHorizontalStart");
  const smallestY = Math.min(...alignableSizedNodes.value.map((i) => i.size.y));
  for (const node of alignableSizedNodes.value) {
    const rounded = snapsToGrid(node)
      ? Math.round(smallestY / 10) * 10
      : smallestY;
    changeSize(node, { y: rounded - node.size.y });
  }
  undoStore.endGroup("model", groupId);
}

function alignVerticalCenter() {
  if (alignableSizedNodes.value.length === 0) return;
  const groupId = undoStore.startGroup("model", "alignHorizontalCenter");
  const smallestY = Math.min(...alignableSizedNodes.value.map((i) => i.size.y));
  const largestY = Math.max(
    ...alignableSizedNodes.value.map((i) => i.size.y + i.size.height)
  );
  const centerY = (smallestY + largestY) / 2;
  for (const node of alignableSizedNodes.value) {
    const itemSize = node.size;
    const rounded = snapsToGrid(node)
      ? Math.round((centerY - itemSize.height / 2) / 10) * 10
      : Math.round(centerY - itemSize.height / 2);
    changeSize(node, { y: rounded - node.size.y });
  }
  undoStore.endGroup("model", groupId);
}

function alignVerticalEnd() {
  if (alignableSizedNodes.value.length === 0) return;
  const groupId = undoStore.startGroup("model", "alignHorizontalEnd");
  const largestY = Math.max(
    ...alignableSizedNodes.value.map((i) => i.size.y + i.size.height)
  );
  for (const node of alignableSizedNodes.value) {
    const rounded = snapsToGrid(node)
      ? Math.round(largestY / 10) * 10
      : largestY;
    changeSize(node, { y: rounded - node.size.y - node.size.height });
  }
  undoStore.endGroup("model", groupId);
}

function distributeHorizontal() {
  if (alignableSizedNodes.value.length <= 2) return;
  const groupId = undoStore.startGroup("model", "distributeHorizontal");
  const sortedNodes = [...alignableSizedNodes.value].sort((a, b) => a.size.x - b.size.x);
  const allSnapping = sortedNodes.every((node) => snapsToGrid(node));
  const firstItem = sortedNodes[0];
  const lastItem = sortedNodes[sortedNodes.length - 1];
  const totalWidth = sortedNodes
    .slice(1, -1)
    .reduce((sum, item) => sum + item.size.width, 0);
  const spaceBetween =
    lastItem.size.x - (firstItem.size.x + firstItem.size.width) - totalWidth;
  let gap = Math.round(spaceBetween / (sortedNodes.length - 1));
  if (allSnapping) gap = Math.round(gap / 10) * 10;

  let currentX = firstItem.size.x + firstItem.size.width + gap;
  for (let i = 1; i < sortedNodes.length; i++) {
    const node = sortedNodes[i];
    const rounded = snapsToGrid(node)
      ? Math.round(currentX / 10) * 10
      : currentX;
    changeSize(node, { x: rounded - node.size.x });
    currentX += node.size.width + gap;
  }

  undoStore.endGroup("model", groupId);
}

function distributeVertical() {
  if (alignableSizedNodes.value.length <= 2) return;
  const groupId = undoStore.startGroup("model", "distributeVertical");
  const sortedNodes = [...alignableSizedNodes.value].sort((a, b) => a.size.y - b.size.y);
  const allSnapping = sortedNodes.every((node) => snapsToGrid(node));
  const firstItem = sortedNodes[0];
  const lastItem = sortedNodes[sortedNodes.length - 1];
  const totalHeight = sortedNodes
    .slice(1, -1)
    .reduce((sum, item) => sum + item.size.height, 0);
  const spaceBetween =
    lastItem.size.y - (firstItem.size.y + firstItem.size.height) - totalHeight;
  let gap = Math.round(spaceBetween / (sortedNodes.length - 1));
  if (allSnapping) gap = Math.round(gap / 10) * 10;

  let currentY = firstItem.size.y + firstItem.size.height + gap;
  for (let i = 1; i < sortedNodes.length; i++) {
    const node = sortedNodes[i];
    const rounded = snapsToGrid(node)
      ? Math.round(currentY / 10) * 10
      : currentY;
    changeSize(node, { y: rounded - node.size.y });
    currentY += node.size.height + gap;
  }

  undoStore.endGroup("model", groupId);
}

function changeSize(treeNode: SceneTreeNode, deltas: { x?: number; y?: number; width?: number; height?: number }) {
  if (isLayerTreeNode(treeNode)) return;

  // TODO: handle groups better
  if (isGroupTreeNode(treeNode)) {
    const childNodes = treeNode.children;
    for (const childNode of childNodes) {
      changeSize(childNode, deltas);
    }
    return;
  }

  const sceneNode = treeNode.node;

  const payload: {
    id: number;
    x?: number;
    y?: number;
    width?: number;
    height?: number;
  } = {
    id: sceneNode.id
  };

  if (deltas.x != null) {
    payload.x = sceneNode.x + deltas.x;
  }
  if (deltas.y != null) {
    payload.y = sceneNode.y + deltas.y;
  }
  if (deltas.width != null) {
    payload.width = sceneNode.width + deltas.width;
  }
  if (deltas.height != null) {
    payload.height = sceneNode.height + deltas.height;
  }

  sceneNodeStore.update(payload);
}

function alignSelectedItemsToGrid() {
  const groupId = undoStore.startGroup("model", "alignToGrid");
  for (const node of alignableSizedNodes.value) {
    const roundedX = Math.round(node.size.x / 10) * 10;
    const roundedY = Math.round(node.size.y / 10) * 10;
    const roundedWidth = Math.round(node.size.width / 10) * 10;
    const roundedHeight = Math.round(node.size.height / 10) * 10;
    changeSize(node, { x: roundedX - node.size.x, y: roundedY - node.size.y, width: roundedWidth - node.size.width, height: roundedHeight - node.size.height });
  }
  undoStore.endGroup("model", groupId);
}

// ==========================
// Hidden

const hidden = computed(() => {
  if (props.impactSourceElement) return true;
  if (selectedItemSizes.value.length <= 0) return true;
  if (props.tool === "textedit") return true;
  if (editingShape.value && onHandle.value) return true;
  return false;
});

// ==========================
// Expanded

const settingsStore = useSettingsStore();
const expansionFixed = computed(() => settingsStore.netActionsBarExtended);
const expanded = ref(expansionFixed.value);
function expand() {
  expanded.value = true;
}
function saveExpansion() {
  settingsStore.set("netActionsBarExtended", true);
}
function collapse() {
  expanded.value = false;
  settingsStore.set("netActionsBarExtended", false);
}

const actionKeys = [
  "relations",
  "flows",
  "alignment",
  "elementType",
  "medium",
  "fill",
  "stroke",
  "textColor",
  "fontSize",
  "fontSizeRelative",
  "locked",
  "gridLocked",
  "aspectRatio",
  "visibility",
  "delete"
];
type ActionKey = (typeof actionKeys)[number];

const essentials = computed(() => {
  const essentials = new Set<ActionKey>();
  if (expanded.value) {
    actionKeys.forEach((key) => essentials.add(key));
    return essentials;
  }

  if (selectedSceneNode.value) {
    const node = selectedSceneNode.value;
    if (isSceneElementNode(node)) {
      essentials.add("relations");
      if (isUidOfType(node.data.elementUid, "target") || isUidOfType(node.data.elementUid, "resource")) {
        essentials.add("flows");
      }
    }
    if (isSceneMediumNode(node)) {
      essentials.add("medium");
    }
    if (isSceneTextNode(node)) {
      essentials.add("fontSize");
      essentials.add("fontSizeRelative");
      essentials.add("textColor");
    }
    if (isSceneShapeNode(node)) {
      essentials.add("fill");
      essentials.add("stroke");
    }
  } else {
    essentials.add("locked");
    essentials.add("alignment");
    essentials.add("fill");
    essentials.add("stroke");
    essentials.add("textColor");
    essentials.add("medium");
  }

  return essentials;
});

watch(hidden, (hidden) => {
  if (hidden) {
    expanded.value = expansionFixed.value;
  }
});
</script>

<template>
  <net-actionbar
    v-if="!hidden"
    :x="dimensions.x"
    :y="dimensions.y - 15"
    :width="dimensions.width"
    :height="dimensions.height"
    class="net-dynamic-actionbar"
    @wheel.prevent="emit('wheel', $event)"
  >
    <template v-if="flowSourceElement">
      <template v-if="selectedSceneNode && isSceneElementNode(selectedSceneNode) && selectedSceneNode.data.elementUid == flowSourceElement.uid">
        <li class="net-actionbar__entry">
          <button
            class="net-actionbar__button"
            type="button"
            :title="$t('actions.createVoidSourceFlow')"
            @click="createVoidFlow(selectedSceneNode.data.elementUid, 'source')"
          >
            <lucide-icon
              name="cloud"
              style="margin-right: -0.125em"
            ></lucide-icon>
            <lucide-icon name="flow"></lucide-icon>
          </button>
        </li>
        <li class="net-actionbar__entry">
          <button
            class="net-actionbar__button"
            type="button"
            :title="$t('actions.createVoidSinkFlow')"
            @click="createVoidFlow(selectedSceneNode.data.elementUid, 'sink')"
          >
            <lucide-icon
              name="flow"
              style="margin-right: -0.125em"
            ></lucide-icon>
            <lucide-icon name="cloud"></lucide-icon>
          </button>
        </li>
      </template>
    </template>
    <template v-else>
      <template v-if="selectedSceneNode">
        <template v-if="isSceneShapeNode(selectedSceneNode)">
          <li class="net-actionbar__entry">
            <button-popout
              :value="selectedSceneNode.data.shapeType"
              :options="shapeTypes"
              x-alignment="left"
              y-alignment="bottom"
              class="net-actionbar__button"
              :title="$t(`shapes.${selectedSceneNode.data.shapeType}`)"
              @update:value="changeShapeType($event)"
            >
              <template #default="{ value, selected }">
                <div
                  class="net-actionbar__typeoption"
                  :style="{
                    color: selected ? 'var(--color-action)' : 'inherit'
                  }"
                >
                  <lucide-icon v-if="value == 'rectangle'" name="square" />
                  <lucide-icon
                    v-else-if="value == 'roundedRectangle'"
                    name="rounded-rectangle"
                  />
                  <lucide-icon v-else-if="value == 'circle'" name="circle" />
                  <lucide-icon
                    v-else-if="value == 'triangle'"
                    name="triangle"
                  />
                  <lucide-icon v-else-if="value == 'polygon'" name="pentagon" />
                  <span>{{ $t(`shapes.${value}`) }}</span>
                </div>
              </template>
              <template #selected="{ value }">
                <lucide-icon v-if="value == 'rectangle'" name="square" />
                <lucide-icon
                  v-else-if="value == 'roundedRectangle'"
                  name="rounded-rectangle"
                />
                <lucide-icon v-else-if="value == 'circle'" name="circle" />
                <lucide-icon v-else-if="value == 'triangle'" name="triangle" />
                <lucide-icon v-else-if="value == 'polygon'" name="pentagon" />
              </template>
            </button-popout>
          </li>

          <li class="net-actionbar__entry">
            <button
              type="button"
              class="net-actionbar__button"
              :class="{ '-active': editingShape }"
              :title="$t('actions.editShape')"
              @click="editShape"
            >
              <lucide-icon name="pen-tool"></lucide-icon>
            </button>
          </li>

          <li
            class="net-actionbar__entry"
            v-if="selectedSceneNode.data.shapeType === 'roundedRectangle'"
          >
            <button
              type="button"
              class="net-actionbar__button"
              :class="{ '-active': editingRadius }"
              :title="$t('actions.editRadius')"
              @click="editRadius"
            >
              <lucide-icon name="square-round-corner"></lucide-icon>
            </button>
          </li>
        </template>
        <template v-else-if="isSceneElementNode(selectedSceneNode)">
          <li class="net-actionbar__entry" v-if="features.impact">
            <button
              class="net-actionbar__button"
              type="button"
              :title="$t('labels.createRelation')"
              @click="emit('toggle-relation-mode', { uid: selectedSceneNode.data.elementUid })"
            >
              <lucide-icon name="impact"></lucide-icon>
            </button>
          </li>
          <li
            v-if="
              features.flow && !isUidOfType(selectedSceneNode.data.elementUid, 'index')
            "
            class="net-actionbar__entry"
          >
            <button
              class="net-actionbar__button"
              type="button"
              :title="$t('actions.createFlow')"
              @click="emit('toggle-flow-mode', { uid: selectedSceneNode.data.elementUid })"
            >
              <lucide-icon name="flow"></lucide-icon>
            </button>
          </li>
        </template>
      </template>

      <li v-if="selectedItem && popupMenuAvailable" class="net-actionbar__entry">
        <button
          class="net-actionbar__button"
          type="button"
          :title="$t('actions.openPopupmenu')"
          @click="openPopupmenu(selectedItem)"
        >
          <lucide-icon name="pencil"></lucide-icon>
        </button>
      </li>

      <li v-if="selectedItem && selectedItem.type === 'relation' && essentials.has('visibility')" class="net-actionbar__entry">
        <button
          v-if="isRelationHidden(selectedItem.id)"
          class="net-actionbar__button"
          type="button"
          :title="$t('actions.show')"
          @click="showRelation(selectedItem.id)"
        >
          <lucide-icon name="eye"></lucide-icon>
        </button>
        <button
          v-else
          class="net-actionbar__button"
          type="button"
          :title="$t('actions.hide')"
          @click="hideRelation(selectedItem.id)"
        >
          <lucide-icon name="eye-off"></lucide-icon>
        </button>
      </li>

      <li v-if="selectedItem && selectedItem.type === 'flow' && essentials.has('visibility')" class="net-actionbar__entry">
        <button
          v-if="isFlowHidden(selectedItem.id)"
          class="net-actionbar__button"
          type="button"
          :title="$t('actions.show')"
          @click="showFlow(selectedItem.id)"
        >
          <lucide-icon name="eye"></lucide-icon>
        </button>
        <button
          v-else
          class="net-actionbar__button"
          type="button"
          :title="$t('actions.hide')"
          @click="hideFlow(selectedItem.id)"
        >
          <lucide-icon name="eye-off"></lucide-icon>
        </button>
      </li>

      <template v-if="!selectedItem && selectedItems.some((item) => item.type === 'relation') && essentials.has('visibility')">
        <li
          v-if="selectedItems.some((item) => item.type === 'relation' && isRelationHidden(item.id))"
          class="net-actionbar__entry"
        >
          <button
            class="net-actionbar__button"
            type="button"
            :title="$t('actions.show')"
            @click="showRelations()"
          >
            <lucide-icon name="eye"></lucide-icon>
          </button>
        </li>
        <li
          v-if="selectedItems.some((item) => item.type === 'relation' && !isRelationHidden(item.id))"
          class="net-actionbar__entry"
        >
          <button
            class="net-actionbar__button"
            type="button"
            @click="hideRelations()"
          >
            <lucide-icon name="eye-off"></lucide-icon>
          </button>
        </li>
      </template>
      <template v-if="!selectedItem && selectedItems.some((item) => item.type === 'flow') && essentials.has('visibility')">
        <li
          v-if="selectedItems.some((item) => item.type === 'flow' && isFlowHidden(item.id))"
          class="net-actionbar__entry"
        >
          <button
            class="net-actionbar__button"
            type="button"
            :title="$t('actions.show')"
            @click="showFlows()"
          >
            <lucide-icon name="eye"></lucide-icon>
          </button>
        </li>
        <li
          v-if="selectedItems.some((item) => item.type === 'flow' && !isFlowHidden(item.id))"
          class="net-actionbar__entry"
        >
          <button
            class="net-actionbar__button"
            type="button"
            @click="hideFlows()"
          >
            <lucide-icon name="eye-off"></lucide-icon>
          </button>
        </li>
      </template>

      <li
        class="net-actionbar__entry"
        v-if="selectedSceneNode && isSceneElementNode(selectedSceneNode) && expanded"
      >
        <button-popout
          :value="splitUid(selectedSceneNode.data.elementUid).type"
          :options="availableElementTypes"
          x-alignment="left"
          y-alignment="bottom"
          class="net-actionbar__button"
          :title="$t(`objectNames.${splitUid(selectedSceneNode.data.elementUid).type}`)"
          @update:value="
            changeElementType(selectedSceneNode.data.elementUid, $event)
          "
        >
          <template #default="{ value, selected }">
            <div
              class="net-actionbar__typeoption"
              :style="{
                color: selected ? 'var(--color-action)' : 'inherit'
              }"
            >
              <lucide-icon :name="value" /><span>{{
                $t(`objectNames.${value}`)
              }}</span>
            </div>
          </template>
          <template #selected="{ value }">
            <lucide-icon :name="value" />
          </template>
        </button-popout>
      </li>

      <tooltip-button
        v-if="alignableNodes.length > 0 && alignableNodes.length > 1"
        class="net-actionbar__button"
        :title="$t('labels.alignment')"
        placement="top"
      >
        <AlignStartVerticalIcon :size="18" />
        <template #tooltip>
          <div class="column">
            <div class="line">
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.alignHorizontalStart')"
                @click="alignHorizontalStart()"
              >
                <AlignStartVerticalIcon :size="18" />
              </button>
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.alignHorizontalCenter')"
                @click="alignHorizontalCenter()"
              >
                <AlignCenterVerticalIcon :size="18" />
              </button>
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.alignHorizontalEnd')"
                @click="alignHorizontalEnd()"
              >
                <AlignEndVerticalIcon :size="18" />
              </button>
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.distributeHorizontal')"
                @click="distributeHorizontal()"
              >
                <AlignHorizontalSpaceBetweenIcon :size="18" />
              </button>
            </div>
            <div class="line">
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.alignVerticalStart')"
                @click="alignVerticalStart()"
              >
                <AlignStartHorizontalIcon :size="18" />
              </button>
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.alignVerticalCenter')"
                @click="alignVerticalCenter()"
              >
                <AlignCenterHorizontalIcon :size="18" />
              </button>
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.alignVerticalEnd')"
                @click="alignVerticalEnd()"
              >
                <AlignEndHorizontalIcon :size="18" />
              </button>
              <button
                type="button"
                class="net-actionbar__button"
                :title="$t('actions.distributeVertical')"
                @click="distributeVertical()"
              >
                <AlignVerticalSpaceBetweenIcon :size="18" />
              </button>
            </div>
          </div>
        </template>
      </tooltip-button>

      <li v-if="hasFill && essentials.has('fill')" class="net-actionbar__entry">
        <tooltip-button
          class="net-actionbar__button"
          :title="$t('labels.fill')"
          placement="top"
        >
          <FillSymbol :fills="fills" />
          <template #tooltip>
            <div class="column p-2">
              <div>
                <label class="label" for="fillColor">
                  {{ $t("labels.fillColor") }}
                </label>
                <color-picker
                  id="fillColor"
                  :value="fill"
                  :null-allowed="true"
                  @update:value="fill = $event"
                />
              </div>
              <div>
                <label class="label" for="fillOpacity">
                  {{ $t("labels.opacity") }}
                </label>
                <input-range
                  id="fillOpacity"
                  :value="fillOpacity"
                  :min="0"
                  :max="100"
                  :step="1"
                  :colors="[fillFullOpacity]"
                  :style="{
                    filter: 'drop-shadow(0 0 1px rgba(0, 0, 0, 0.2))'
                  }"
                  @update:value="fillOpacity = $event"
                />
              </div>
            </div>
          </template>
        </tooltip-button>
      </li>

      <li
        v-if="hasStroke && essentials.has('stroke')"
        class="net-actionbar__entry"
      >
        <tooltip-button
          class="net-actionbar__button"
          :title="$t('labels.stroke')"
          placement="top"
          :style="{ backgroundColor: strokeContrastColor }"
        >
          <svg
            width="16"
            height="16"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect x="1" y="2" width="14" height="2" :fill="stroke" />
            <rect x="1" y="6" width="14" height="3" :fill="stroke" />
            <rect x="1" y="11" width="14" height="4" :fill="stroke" />
          </svg>
          <template #tooltip>
            <div class="column p-2">
              <label class="label" for="strokeWidth">
                {{ $t("labels.strokeWidth") }}
              </label>
              <input-range
                id="strokeWidth"
                :value="strokeWidth"
                :min="0"
                :max="10"
                :step="1"
                @update:value="strokeWidth = $event"
              />

              <div>
                <h4 class="label mb-0">{{ $t("headings.colors") }}</h4>
                <color-picker
                  :value="stroke"
                  :null-allowed="true"
                  :transparent="false"
                  @update:value="stroke = $event || '#000000'"
                />
              </div>
            </div>
          </template>
        </tooltip-button>
      </li>

      <template v-if="hasFontSize && essentials.has('fontSizeRelative')">
        <li class="net-actionbar__entry">
          <tooltip-button
            class="net-actionbar__button"
            :title="$t('labels.fontSize')"
            tooltip-class="tooltip-button__tooltip net-text-toolbar__tooltip"
            placement="top"
          >
            <lucide-icon name="a-arrow-up-down" />
            <template #tooltip>
              <div class="line p-1 gap-1">
                <button
                  class="net-actionbar__button"
                  :title="$t('actions.increaseFontSize')"
                  type="button"
                  @click="increaseFontSize()"
                >
                  <lucide-icon name="a-arrow-up" />
                </button>
                <button
                  class="net-actionbar__button"
                  :title="$t('actions.resetFontSize')"
                  type="button"
                  @click="resetFontSize()"
                >
                  <lucide-icon name="eraser" />
                </button>
                <button
                  class="net-actionbar__button"
                  :title="$t('actions.decreaseFontSize')"
                  type="button"
                  @click="decreaseFontSize()"
                >
                  <lucide-icon name="a-arrow-down" />
                </button>
              </div>
            </template>
          </tooltip-button>
        </li>
      </template>

      <li
        v-if="hasTextColor && essentials.has('textColor')"
        class="net-actionbar__entry"
      >
        <input-color-text
          :value="textColor ?? null"
          :default-value="contrastTextColor"
          class="net-actionbar__input"
          @update:value="textColor = $event"
        />
      </li>

      <template v-if="hasMedium && essentials.has('medium')">
        <li class="net-actionbar__entry">
          <label
            class="net-actionbar__button net-actionbar__uploadbutton"
            :title="$t('actions.replaceMedium')"
          >
            <lucide-icon name="image" />
            <input type="file" @change="onMediaFileChange" />
          </label>
        </li>
      </template>

      <template v-if="selectedSceneNode">
        <li
          v-if="
            selectedSceneNode.dataType == 'medium' &&
            essentials.has('aspectRatio')
          "
          class="net-actionbar__entry"
        >
          <button
            type="button"
            class="net-actionbar__button"
            :title="
              selectedSceneNode.aspectRatioLocked
                ? $t('actions.freeAspectRatio')
                : $t('actions.lockAspectRatio')
            "
            @click="
              updateKey(
                selectedSceneNode,
                'aspectRatioLocked',
                !selectedSceneNode.aspectRatioLocked
              )
            "
          >
            <lucide-icon
              :name="selectedSceneNode.aspectRatioLocked ? 'link-2' : 'unlink-2'"
            ></lucide-icon>
          </button>
        </li>
      </template>

      <li
        v-if="hasLocked && essentials.has('locked')"
        class="net-actionbar__entry"
      >
        <button
          type="button"
          class="net-actionbar__button"
          :class="{ '-active': locked }"
          :title="locked ? $t('actions.unlock') : $t('actions.lock')"
          @click="locked = !locked"
        >
          <lucide-icon :name="locked ? 'lock' : 'lock-open'"></lucide-icon>
        </button>
      </li>

      <li
        v-if="hasGridLocked && essentials.has('gridLocked')"
        class="net-actionbar__entry"
      >
        <button
          type="button"
          class="net-actionbar__button"
          :class="{ '-active': gridLocked }"
          :title="gridLocked ? $t('actions.unlockFromGrid') : $t('actions.lockToGrid')"
          @click="gridLocked = !gridLocked"
        >
          <lucide-icon :name="gridLocked ? 'grid-2-x-2-check' : 'grid-2-x-2'"></lucide-icon>
        </button>
      </li>


      <li class="net-actionbar__entry" v-if="essentials.has('delete')">
        <button
          type="button"
          class="net-actionbar__button"
          :title="$t('actions.delete')"
          @click="deleteItems"
        >
          <lucide-icon name="trash-2"></lucide-icon>
        </button>
      </li>

      <div class="separator"></div>

      <li class="net-actionbar__entry" v-if="!expanded">
        <button type="button" class="net-actionbar__button" @click="expand()">
          <lucide-icon name="ellipsis"></lucide-icon>
        </button>
      </li>

      <li class="net-actionbar__entry" v-if="expanded">
        <button
          v-if="expansionFixed"
          type="button"
          class="net-actionbar__button -light"
          @click="collapse()"
        >
          <lucide-icon name="pin-off"></lucide-icon>
        </button>
        <button
          v-if="!expansionFixed"
          type="button"
          class="net-actionbar__button -light"
          @click="saveExpansion()"
        >
          <lucide-icon name="pin"></lucide-icon>
        </button>
      </li>
    </template>
  </net-actionbar>
</template>

<style lang="scss">
.net-actionbar {
  &__typeoption {
    display: grid;
    grid-template-columns: min-content 1fr;
    gap: 0.5rem;
    text-align: left;
    align-items: center;
  }

  &__tooltip {
    background-color: var(--color-background-deep);
    box-shadow:
      0 2px 10px rgba(0, 0, 0, 0.12),
      0 2px 5px rgba(0, 0, 0, 0.16);
    padding: 0.75rem;
    border-radius: 0.5rem;
  }

  .net-actionbar__button svg {
    pointer-events: none;
  }
}
</style>
